import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
// import camelCaseKeys from 'camelcase-keys';
// import * as pascalCaseKeys from 'camelcase-keys';
// import snakeCaseKeys from 'snakecase-keys';
// import pascalcaseKeys from 'pascalcase-keys';
import deepMapKeys from 'deep-map-keys';
import { camelCase, snakeCase, upperFirst } from 'lodash';
import { Case, ManuteRequestConfig } from '@/api/types';
import ManuteAuthToken from '@/api/src/token';
import { REFRESH_TOKEN } from '@/api/requests/auth/auth';
import router from '@/router';

const REQUEST_DEFAULTS: ManuteRequestConfig = {
    requiresAuth: true,
    transformRequestKeysTo: 'SNAKE',
    transformResponseKeysTo: 'CAMEL',
};

export class ManuteApi {
    /**
     * @var {AxiosInstance}
     */
    instance: AxiosInstance;

    /**
     * Api class constructor.
     * @param config
     */
    constructor(config?: AxiosRequestConfig) {
        // Initializing axios instance with default configurations.
        this.instance = axios.create(config);
        // Installing interceptor to handle 401 unauthenticated error.
        this.installUnauthenticatedInterceptor();
    }

    /**
     * Perform the HTTP request.
     * @param config
     */
    call(config: ManuteRequestConfig) {
        config = { ...REQUEST_DEFAULTS, ...config };
        if (config.requiresAuth) {
            config = this.addAuthorizationHeader(config);
        }
        if (config.transformRequestKeysTo) {
            config = this.transformRequestKeys(config);
        }
        if (config.transformResponseKeysTo) {
            config = this.transformResponseKeys(config);
        }
        return this.instance.request(config);
    }

    /**
     * Add authorization header with bearer token.
     * @param config
     */
    private addAuthorizationHeader(config: AxiosRequestConfig) {
        const token = ManuteAuthToken.fromStorage();
        config.headers = {
            ...this.instance.defaults.headers,
            Authorization: `Bearer ${token.accessToken}`,
        };
        return config;
    }

    /**
     * Retrieve cast function name.
     * @param {*} data
     * @param {Case} castMethod
     */
    private getCastedData(data: any, castMethod?: Case) {
        const method = castMethod ? castMethod.toLowerCase() : '';
        switch (method) {
            case 'camel':
                // data = camelCaseKeys(data, { deep: true });
                data = deepMapKeys(data, camelCase);
                break;
            case 'pascal':
                // data = pascalcaseKeys(data);  // pascalCaseKeys(data, { deep: true, pascalCase: true });
                data = deepMapKeys(data, (key: string) => upperFirst(camelCase(key)));
                break;
            case 'snake':
                // data = snakeCaseKeys(data, { deep: true });
                data = deepMapKeys(data, snakeCase);
                break;
        }
        return data;
    }

    /**
     * Install interceptor to handle 401 Unauthenticated error.
     */
    private installUnauthenticatedInterceptor() {
        this.instance.interceptors.response.use((response) => response, async (error) => {
            console.error(error.response);
            // Original request
            const request = {
                ...error.response.config,
                data: error.response.config.data ? JSON.parse(error.response.config.data) : null,
            };

            if (error.response.status === 401 && !!request && !request.__isRetryRequest) {
                const token = ManuteAuthToken.fromStorage();

                if (request.url.replace(request.baseURL, '') !== '/oauth/token' && token.isExpired) {
                    const response = await this.call(REFRESH_TOKEN(token.refreshToken));
                    const refreshedToken = ManuteAuthToken.fromHttp(response.data);
                    // Store the token in localstorage.
                    refreshedToken.toStorage();

                    // Retry to call the original request, with the new access token.
                    request.__isRetryRequest = true;
                    request.requiresAuth = true;
                    return this.call(request);
                }

                // Otherwise, reset token is local storage
                ManuteAuthToken.removeFromStorage();
                // TODO: move this method somewhere else
                router.replace({ name: 'login' });
            }

            return Promise.reject(error);
        });
    }

    /**
     * Apply cast keys function
     * @param {ManuteRequestConfig} config
     */
    private transformRequestKeys(config: ManuteRequestConfig) {
        if (config.transformRequestKeysTo) {
            config.transformRequest = [
                (data) => !!data && !(data instanceof FormData)
                    ? this.getCastedData(data, config.transformRequestKeysTo)
                    : data,
                // @ts-ignore
                ...this.instance.defaults.transformRequest,
            ];
        }
        return config;
    }

    /**
     * Apply cast keys function
     * @param {ManuteRequestConfig} config
     */
    private transformResponseKeys(config: ManuteRequestConfig) {
        if (config.transformResponseKeysTo) {
            config.transformResponse = [
                ...this.instance.defaults.transformResponse,
                (data) => !!data && !(data instanceof Blob) ? this.getCastedData(data, config.transformResponseKeysTo) : data,
            ];
        }
        return config;
    }
}
